import React, { createContext, ReactNode, useContext, useState } from "react";
import {
  MD3LightTheme,
  MD3DarkTheme,
  Provider as PaperProvider,
  configureFonts
} from "react-native-paper";
import { MD3Type } from "react-native-paper/lib/typescript/types";
import { useThemeStore } from "./store/themeStore";


const typescale: Record<string, MD3Type> = {
  // Title/Header 1
  headlineLarge: {
    fontFamily: 'Poppins-Medium', fontWeight: '500', fontSize: 20, lineHeight: 26, letterSpacing: 1,
  },
  // Header 2
  headlineMedium: {
    fontFamily: 'Poppins-Medium', fontWeight: '500', fontSize: 16, lineHeight: 22.4, letterSpacing: 1,
  },
  // Header 3
  headlineSmall: {
    fontFamily: 'Poppins-Medium', fontWeight: '400', fontSize: 14, lineHeight: 16, letterSpacing: 0.2,
  },
  // Subtitle/Body Large
  titleSmall: {
    fontFamily: 'Poppins-Regular', fontWeight: '400', fontSize: 10, lineHeight: 16, letterSpacing: 0.5,
  },
  // Body
  bodyMedium: {
    // fontFamily: 'Poppins-Regular', fontWeight: '400', fontSize: 14, lineHeight: 19.2, letterSpacing: 1,
  },
  // Body
  bodySmall: {
    fontFamily: 'Poppins-Regular', fontWeight: '400', fontSize: 13, lineHeight: 17, letterSpacing: 1,
  },
};
const lightColors: AppColorTokens = {
  btnBgFill: "#FEF3E8",
  buttonPrimary: "#FEE6D4",

  error: "#E81212",
  success: "#09671D",
  opposed: "#FF7017",
  neutral: "#7E7762",
  uncertain: "#FFC415",

  surfaceBg: "rgba(255,255,255,0.7)",
  surfaceColor: "#FFFFFF",
  chatBgUser: "rgba(255,196,137,0.40)",
  chatBgAssistant: "rgba(254,184,165,0.50)",

  rippleCardColor: "#FEF3E8",

  textPrimary: "#221201",

  headerText: "#30251D",
  bodyText: "#30251D",
  subtleText: "#999DA3",

  primary: "#8F3E47",
  primaryLight: "rgba(143,62,71,0.7)",
  primaryLight2: "rgba(143,62,71,0.2)",
  primaryLight3: "#FEF3E8",

  secondary: "#CC7722",
  secondaryLight: "#CC772295",
  secondaryLight2: "#e1b72c",
  brandHope: "#1E6685",
  shareCard1Gradient1: "#FFFFFF",
  shareCard1Gradient2: "#FFACDC",
  shareCard2Gradient1: "#B1BEFE",
  shareCard2Gradient2: "#FFE788",
  shareCard3Gradient1: "#CE94FF",
  shareCard3Gradient2: "#BADFFF",
};

const darkColors: AppColorTokens = {
  btnBgFill: "rgba(5,7,14,0.5)",
  buttonPrimary: "rgba(0,0,0,0.2)",

  error: "#E81212",
  success: "#09671D",
  opposed: "#FF7017",
  neutral: "#CAC6B9",
  uncertain: "#FFC415",

  surfaceBg: "#02030B",
  surfaceColor: "#02030B",
  chatBgUser: "rgba(255,196,137,0.40)",
  chatBgAssistant: "rgba(5,7,14,0.20)",

  rippleCardColor: "#221201",

  textPrimary: "#FFFFFF",
  headerText: "#FFFFFF",
  bodyText: "#FFFFFF",
  subtleText: "#BBBBBB",

  primary: "#E1B72C",
  primaryLight: "rgba(225,183,44,0.9)",
  primaryLight2: "rgba(225,183,44,0.2)",
  primaryLight3: "rgba(0,0,0,0.2)",

  secondary: "#C49508",
  secondaryLight: "#C4950895",
  secondaryLight2: "#C4950850",
  brandHope: "#1E6685",
  shareCard1Gradient1: "#00091F",
  shareCard1Gradient2: "#150B01",
  shareCard2Gradient1: "#023626",
  shareCard2Gradient2: "#220122",
  shareCard3Gradient1: "#2E1065",
  shareCard3Gradient2: "#020617"
};
const commonColors = {
  white: '#FFFFFF',
  black: '#000000',
  lightPrimary: '#8F3E47',
  lightTextPrimary: '#221201',
  grey: '#f5f0ea',
  greyBase: "#ACACAC",
  grey2: "#4F4F4F",

}
const thinkoLightTheme = {
  ...MD3LightTheme,
  dark: false,
  colors: {
    ...MD3LightTheme.colors,
    ...lightColors,
    ...commonColors,
    appBackground1: "#FFFFFF",
    appBackground2: "#FFD2B2",
    appBackground3: "#FFECDF",
    appBackground2Web: "#FEE5D3",
    appBackground3Web: "#B6E0F5",

    footerGradient1: '#FFFFFF',
    footerGradient2: '#F5E3AA',

    appCard1: "#FFD2B2",
    appCard2: "#FFECDF",
    appCard1Web: "#fff5ec",
    appCard2Web: "#e5f1f8",

    bubbleColor1 : "#011936",
    bubbleColor2 : "#512D38",
    bubbleColor3 : "#1A281F",

  },
  fonts: configureFonts({ config: typescale }),
};


const thinkoDarkTheme = {
  ...MD3DarkTheme,
  dark: true,
  colors: {
    ...MD3DarkTheme.colors,

    ...darkColors,
    ...commonColors,
    appBackground1: "#011312",
    appBackground2: "#221201",
    appBackground3: "#101436",
    appBackground2Web: "#221201",
    appBackground3Web: "#101436",


    footerGradient1: '#07091c',
    footerGradient2: '#221201',

    appCard1: "#43382c",
    appCard2: "#221201",
    appCard1Web: "#43382c",
    appCard2Web: "#221201",

    
    bubbleColor1 : "#F8D362",
    bubbleColor2 : "#C87E87",
    bubbleColor3 : "#BAD7F2",

    
  },
  fonts: configureFonts({ config: typescale }),
};

interface ThemeContextProps {
  isDark: boolean;
  toggleTheme: () => void;
  theme: typeof thinkoLightTheme | typeof thinkoDarkTheme;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useThemeSettings = () => {
  const context = useContext(ThemeContext);
  if (!context)
    throw new Error("useThemeSettings must be used within ThemeProvider");
  return context;
};

const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const { isDark, toggleTheme } = useThemeStore();
  const theme = isDark ? thinkoDarkTheme : thinkoLightTheme;

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, theme }}>
      <PaperProvider theme={theme}>{children}</PaperProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;

export type AppColorTokens = {
  btnBgFill: string;
  buttonPrimary: string;

  error: string;
  success: string;
  opposed: string;
  neutral: string;
  uncertain: string;

  surfaceBg: string;
  surfaceColor: string;
  chatBgUser: string;
  chatBgAssistant: string;

  rippleCardColor: string;

  textPrimary: string;
  headerText: string,
  bodyText: string,
  subtleText: string,

  primary: string;
  primaryLight: string;
  primaryLight2: string;
  primaryLight3: string;

  secondary: string;

  secondaryLight: string,
  secondaryLight2: string,
  brandHope: string;

  shareCard1Gradient1: string;
  shareCard1Gradient2: string;
  shareCard2Gradient1: string;
  shareCard2Gradient2: string;
  shareCard3Gradient1: string;
  shareCard3Gradient2: string;

};
